/* ============================================
   Cotizaciones de la cartera — lo que el partner vende

   Es la otra mitad del pipeline: la oportunidad dice que hay un trato, esto
   dice con qué documento se está pidiendo la firma. Va aparte de
   `PortalCotizaciones` —la del portal— por una razón de datos, no de estilo:
   esa filtra por `cliente_id` = la empresa del token, o sea la del propio
   partner, y acá lo que importa son las de sus CLIENTES. Montar la misma
   página en dos rutas habría mostrado una lista vacía.

   El documento se abre por su enlace público (`/ver/:token`): es exactamente
   lo que el partner le manda a su cliente, con la marca que lleve la
   cotización, así que no hay una segunda versión que mantener.
   ============================================ */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { useAuth } from '@/hooks/useAuth'
import { formatCurrency, formatDate, r2 } from '@/lib/utils'
import { llevaPreciosBase } from '@/lib/markup'
import type { Cliente, Cotizacion, CotizacionItemsEstandar, EstadoCotizacion } from '@/lib/types'
import { Badge, KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import CotizacionVisor from '@/components/crm/CotizacionVisor'
import SubirCotizacion from '@/components/cotizaciones/SubirCotizacion'
import type { BadgeVariant } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import EmptyState from '@/components/shared/EmptyState'
import { IconExternalLink, IconFileText, IconSearch, IconEye, IconUpload, IconPlus, IconTag } from '@/components/icons/Icons'

const CRUMBS = [{ label: 'CRM' }, { label: 'Cotizaciones' }]
const TODAS = 'todas'

const ESTADO: Record<string, { label: string; variant: BadgeVariant }> = {
  borrador:       { label: 'Borrador',       variant: 'borrador' },
  enviada:        { label: 'Enviada',        variant: 'enviada' },
  en_negociacion: { label: 'En negociación', variant: 'warning' },
  aceptada:       { label: 'Aceptada',       variant: 'aceptada' },
  rechazada:      { label: 'Rechazada',      variant: 'rechazada' },
}

const TIPO: Record<string, string> = {
  estandar: 'Estándar',
  equipo_dedicado: 'Equipo dedicado',
  tareas: 'Por tareas',
}

/** Suma por moneda: dos monedas no se suman, se muestran en filas distintas. */
function totalesPorMoneda(cots: Cotizacion[]): [string, number][] {
  const acc = new Map<string, number>()
  for (const c of cots) acc.set(c.moneda, (acc.get(c.moneda) ?? 0) + Number(c.total || 0))
  return [...acc.entries()].sort((a, b) => b[1] - a[1])
}

/**
 * Lo que esta cotización le cuesta al partner: la suma de lo que nos debe por
 * los ítems que salieron de NUESTRO catálogo.
 *
 * Los ítems sin `costo_partner` son suyos —servicios que él hace— y no cuestan
 * nada por nuestro lado, así que no entran en la cuenta. Una cotización donde
 * todo es suyo da costo 0, y su margen es el total.
 *
 * El número sale de `items`, que es lo que ya se guarda: no hay ninguna tabla
 * nueva detrás de esto, solo leer lo que el editor escribió.
 */
/**
 * Lo que le queda al partner: lo que cobra MENOS lo que nos paga.
 *
 * Contra el **subtotal** y no contra el total: `costo_partner` es un importe
 * sin IGV, y restarlo del total con IGV regalaba el impuesto como margen.
 * Una cotización puesta al costo mostraba margen donde no lo hay.
 */
function margenDeLaCotizacion(c: Cotizacion, costo: number): number {
  const neto = r2(Number(c.subtotal || 0) - Number(c.descuento_monto || 0))
  return r2(neto - costo)
}

function costoDeLaCotizacion(c: Cotizacion): { monto: number; moneda: string } {
  if (!Array.isArray(c.items)) return { monto: 0, moneda: c.moneda }
  const items = (c.items as CotizacionItemsEstandar[]).filter(it => Number(it?.costo_partner) > 0)
  if (items.length === 0) return { monto: 0, moneda: c.moneda }
  return {
    monto: r2(items.reduce((acc, it) => acc + Number(it.costo_partner) * (Number(it?.cantidad) || 0), 0)),
    // La del primer ítem costeado: en la práctica todos los servicios que le
    // habilitamos a un partner están en la misma moneda.
    moneda: items[0].costo_partner_moneda || c.moneda,
  }
}

export default function PartnerCotizaciones() {
  // Via `useCachedQuery` y no un `useEffect` propio: la lista queda en cache 5
  // minutos y sobrevive al cambio de pestaña, asi que volver aca no vuelve a
  // pedir nada ni parpadea un "Cargando". Ademas comparte la clave `clientes`
  // con Empresas, que ya la tiene traida.
  const { data: todas = [], isLoading: cargandoCots } = useCachedQuery<Cotizacion>('cotizaciones')
  // `/api/clientes` devuelve SOLO la cartera del partner (su propia fila no esta
  // ahi), asi que esa lista es la que decide cuales cotizaciones son de su
  // negocio y cuales son las que le enviamos a el.
  const { data: cartera = [], isLoading: cargandoClis } = useCachedQuery<Cliente>('clientes')
  const loading = cargandoCots || cargandoClis

  // La cotización abierta. Vive acá y no en una ruta: es la misma pantalla
  // mirando una fila de cerca, no otro lugar del panel.
  const [verId, setVerId] = useState<number | null>(null)
  const [subiendo, setSubiendo] = useState(false)
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const { clienteId } = useAuth()
  const [filtroEstado, setFiltroEstado] = useState(TODAS)
  const [filtroEmpresa, setFiltroEmpresa] = useState(TODAS)

  const nombreEmpresa = useMemo(() => {
    const m = new Map<number, string>()
    for (const c of cartera) m.set(c.id, c.empresa || `Empresa #${c.id}`)
    return m
  }, [cartera])

  const cotizaciones = useMemo(() => {
    const idsCartera = new Set(cartera.map(c => c.id))
    /*
      Las de su cartera, y también **las que cuelgan de una oportunidad sin
      empresa todavía**: un partner cotiza mucho antes de que ese cliente
      exista en el directorio, y exigir `cliente_id` las dejaba fuera de esta
      pantalla — le pasó a la 260033 de Grupo Central, que no es cliente.

      No hace falta volver a comprobar de quién es: el backend ya recortó por
      cartera (empresa O oportunidad) antes de devolverlas. Lo único que se
      filtra acá son las de la PROPIA cuenta del partner, que tienen su
      `cliente_id` y no son ventas suyas a terceros.
    */
    return todas.filter(c =>
      c.cliente_id == null ? true : (c.cliente_id !== clienteId && idsCartera.has(c.cliente_id)))
  }, [todas, cartera, clienteId])

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase()
    return cotizaciones.filter(c => {
      if (filtroEstado !== TODAS && c.estado !== filtroEstado) return false
      if (filtroEmpresa !== TODAS && String(c.cliente_id) !== filtroEmpresa) return false
      if (!q) return true
      const empresa = c.cliente_id ? nombreEmpresa.get(c.cliente_id) : ''
      return [c.numero, empresa, c.proyecto_nombre, c.branding?.nombre]
        .some(campo => campo?.toLowerCase().includes(q))
    })
  }, [cotizaciones, search, filtroEstado, filtroEmpresa, nombreEmpresa])

  const firmaFiltros = `${search}|${filtroEstado}|${filtroEmpresa}`
  const { page, setPage, totalPages, paginated, pageSize, total } = usePagination(filtradas, 15, firmaFiltros)

  const resumen = useMemo(() => {
    const aceptadas = cotizaciones.filter(c => c.estado === 'aceptada')
    const enCurso = cotizaciones.filter(c => c.estado === 'enviada' || c.estado === 'en_negociacion')
    /*
      El margen se calcula SOLO sobre las aceptadas: lo demás todavía no es
      plata de nadie, y un margen que incluya lo que no se cerró dice más de lo
      que hay. Por moneda, como el resto — sumar soles con dólares da un
      número que no existe.
    */
    const costoPorMoneda = new Map<string, number>()
    for (const c of aceptadas) {
      const { monto, moneda } = costoDeLaCotizacion(c)
      // Agrupa por la moneda del COSTO, no por la de la venta: lo que nos
      // debe por una cotización en soles puede estar en dólares.
      if (monto > 0) costoPorMoneda.set(moneda, r2((costoPorMoneda.get(moneda) ?? 0) + monto))
    }
    return {
      total: cotizaciones.length,
      aceptadas: aceptadas.length,
      enCurso: enCurso.length,
      montoAceptado: totalesPorMoneda(aceptadas),
      montoEnCurso: totalesPorMoneda(enCurso),
      costoAceptado: costoPorMoneda,
    }
  }, [cotizaciones])

  const monedas = useMemo(
    () => [...new Set([...resumen.montoAceptado, ...resumen.montoEnCurso].map(([m]) => m))],
    [resumen],
  )

  const hayFiltros = search.trim() !== '' || filtroEstado !== TODAS || filtroEmpresa !== TODAS
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado(TODAS); setFiltroEmpresa(TODAS) }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div className="empty-state"><p>Cargando cotizaciones...</p></div>
    </div>
  )

  const abierta = cotizaciones.find(c => c.id === verId) || null
  if (abierta) {
    return (
      <div className="page-enter page-shell">
        <CotizacionVisor
          cotizacion={abierta}
          empresa={abierta.cliente_id ? nombreEmpresa.get(abierta.cliente_id) : undefined}
          onVolver={() => setVerId(null)}
        />
      </div>
    )
  }

  return (
    <div className="page-enter page-shell" id="partner-cotizaciones">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === cotizaciones.length
          ? `${cotizaciones.length} ${cotizaciones.length === 1 ? 'cotización' : 'cotizaciones'}`
          : `${total} de ${cotizaciones.length}`}
        actions={
          <>
            <Link to="/partner/cotizaciones/nueva" className="btn btn-sm btn-primary">
              <IconPlus size={14} /> Nueva cotización
            </Link>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSubiendo(true)}>
              <IconUpload size={14} /> Subir un PDF
            </button>
            {/* Las dos listas no se mezclan —acá él emite, allá recibe, y un total
                que las sume no significa nada— pero sí se encuentran: sin este
                enlace, las nuestras había que ir a buscarlas al otro grupo del
                menú sabiendo de antemano que existen. */}
            <Link to="/partner/cotizaciones" className="btn btn-sm btn-secondary">
              Las que tengo con 1Conecta
              <IconExternalLink size={14} />
            </Link>
          </>
        }
      />

      {cotizaciones.length > 0 && (
        <KpiStrip>
          <KpiRow>
            <StatCard label="Cotizaciones" value={resumen.total} />
            <StatCard label="Aceptadas" value={resumen.aceptadas} tone="success" />
            <StatCard label="En curso" value={resumen.enCurso} tone={resumen.enCurso > 0 ? 'info' : 'default'} />
          </KpiRow>
          {/* Una fila por moneda: sumar USD con PEN da un número que no existe. */}
          {monedas.map(moneda => (
            <KpiRow key={moneda} moneda={moneda}>
              <StatCard
                label="Aceptado"
                value={formatCurrency(resumen.montoAceptado.find(([m]) => m === moneda)?.[1] ?? 0, moneda)}
                tone="success"
              />
              <StatCard
                label="En curso"
                value={formatCurrency(resumen.montoEnCurso.find(([m]) => m === moneda)?.[1] ?? 0, moneda)}
              />
              {/*
                Lo que de lo aceptado nos debe a nosotros. **Es la suma de los
                costos y NO un margen**, y la diferencia importa: un margen
                global saldría de restarle esos costos a TODO lo aceptado, y
                las cotizaciones de antes de que existiera `costo_partner` no
                tienen costo registrado — la de Nordic, por ejemplo. Contarlas
                como si fueran íntegras suyas infla el número con trabajo
                nuestro que nadie costeó.

                El margen por cotización sí es exacto y está en la columna, que
                muestra «—» justamente cuando no hay costo que restar.
              */}
              {(resumen.costoAceptado.get(moneda) ?? 0) > 0 && (
                <StatCard
                  label="Nos debes"
                  value={formatCurrency(resumen.costoAceptado.get(moneda) ?? 0, moneda)}
                  tone="warning"
                />
              )}
            </KpiRow>
          ))}
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            className="form-input"
            placeholder="Buscar por número, empresa o marca..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 170 }}>
          <option value={TODAS}>Todo estado</option>
          {Object.entries(ESTADO).map(([valor, { label }]) => (
            <option key={valor} value={valor}>{label}</option>
          ))}
        </select>
        <select className="form-input" value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)} style={{ width: 200 }}>
          <option value={TODAS}>Toda empresa</option>
          {cartera.map(c => <option key={c.id} value={String(c.id)}>{c.empresa}</option>)}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {subiendo && (
        <SubirCotizacion
          cartera={cartera}
          onCerrar={() => setSubiendo(false)}
          onSubida={cot => {
            setSubiendo(false)
            // La lista está en cache 5 minutos: sin invalidarla, el documento
            // recién subido no aparecería hasta que la cache venza.
            queryClient.invalidateQueries({ queryKey: ['cotizaciones'] })
            setVerId(cot.id)
          }}
        />
      )}

      {cotizaciones.length === 0 ? (
        <EmptyState
          icon={<IconFileText size={48} />}
          title="Sin cotizaciones"
          description="Acá aparecen las cotizaciones de las empresas de tu cartera, con la marca que lleve cada documento. Si ya tienes el documento hecho, sube el PDF y queda con su enlace para compartir; si prefieres que lo armemos nosotros, pídelo desde la oportunidad."
          actionLabel="Subir cotización"
          onAction={() => setSubiendo(true)}
        />
      ) : filtradas.length === 0 ? (
        <div className="empty-state">
          <h3>Ninguna cotización coincide</h3>
          <p>Prueba con otros filtros.</p>
          <button className="btn btn-secondary" onClick={limpiarFiltros}>Limpiar filtros</button>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Cotización</th>
                  <th>Empresa</th>
                  <th>Marca</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>Tu margen</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginated.map(c => {
                  const estado = ESTADO[c.estado as EstadoCotizacion] ?? { label: c.estado, variant: 'neutral' as BadgeVariant }
                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="cell-stack">
                          <span className="cell-stack__title">{c.numero}</span>
                          <span className="cell-stack__sub">{formatDate(c.fecha_emision)}</span>
                        </div>
                      </td>
                      <td>{c.cliente_id ? nombreEmpresa.get(c.cliente_id) : (c.cliente_nombre || '—')}</td>
                      {/* Sin marca propia el documento sale con la nuestra. */}
                      <td>{c.branding?.nombre || '—'}</td>
                      <td>{TIPO[c.tipo_cotizacion] || c.tipo_cotizacion}</td>
                      <td>
                        <Badge variant={estado.variant}>{estado.label}</Badge>
                        {/* Lo que hay que ver de un vistazo en la lista: esta
                            todavia lleva nuestros precios. */}
                        {llevaPreciosBase(c) && <Badge variant="warning">Precio base</Badge>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {/* Una propuesta de alcance no lleva importes: mostrar un
                            total que el documento oculta sería contradecirlo. */}
                        {c.mostrar_precios === false ? '—' : formatCurrency(Number(c.total || 0), c.moneda)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {/* Sin nada nuestro adentro no hay costo que restar, y
                            «el margen es el total» no es información: es la
                            misma cifra de la columna de al lado. */}
                        {(() => {
                          const { monto, moneda } = costoDeLaCotizacion(c)
                          if (c.mostrar_precios === false || monto <= 0) return <span className="cot-margen__vacio">—</span>
                          /*
                            Con el costo en otra moneda el margen no se puede
                            calcular sin un tipo de cambio, así que se muestra
                            solo lo que nos debe. Restar soles menos dólares
                            daría un número que no existe.
                          */
                          if (moneda !== c.moneda) {
                            return (
                              <div className="cell-stack">
                                <span className="cell-stack__title cot-margen__vacio">—</span>
                                <span className="cell-stack__sub">nos debes {formatCurrency(monto, moneda)}</span>
                              </div>
                            )
                          }
                          return (
                            <div className="cell-stack">
                              <span className="cell-stack__title">
                                {formatCurrency(margenDeLaCotizacion(c, monto), c.moneda)}
                              </span>
                              <span className="cell-stack__sub">
                                nos debes {formatCurrency(monto, moneda)}
                              </span>
                            </div>
                          )
                        })()}
                      </td>
                      <td>
                        <div className="row-actions row-actions--nowrap">
                          {/* Abre DENTRO del panel. El enlace a la pestaña
                              aparte sigue existiendo, dentro del visor, que es
                              donde hace falta: para mandárselo al cliente. */}
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setVerId(c.id)}
                            title="Ver el documento"
                          >
                            <IconEye size={13} /> Ver
                          </button>
                          {/* La puerta al markup. Faltaba: el clic en la fila
                              llevaba al documento, así que una cotización que
                              armamos nosotros se podía mirar pero no poner a
                              precio. El editor ya existía y nada lo enlazaba. */}
                          {llevaPreciosBase(c) && (
                            <Link
                              to={`/partner/cotizaciones/${c.id}/editar`}
                              className="btn btn-sm btn-primary"
                              title="Poner tu precio sobre nuestro costo"
                            >
                              <IconTag size={13} /> Poner precio
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page} totalPages={totalPages} total={total} pageSize={pageSize}
            onPage={setPage} noun="cotizaciones"
          />
        </>
      )}
    </div>
  )
}
